import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { HighwayArea } from '@thive/fddm-types';

import { Accordion } from '@/modules/common/components/Accordion';
import { useAccordionExpanded } from '@/modules/userPreferences/hooks';
import { AccordionAddIconButton } from '@/modules/common/components/AccordionAddIconButton';
import { WorkspaceMode } from '@modules/common/types/general';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { JitRoutingPoints } from './JitRoutingPoints';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { UserPreferenceName } from '@/modules/userPreferences';

interface Props {
  selectedHighway: HighwayArea;
  /** With more than one highway selected the points are hidden and adding is disabled. */
  isMultiSelection: boolean;
}

/** @description Translation key explaining why a JIT routing point can't be added, or undefined when it can. */
const getAddDisabledReasonKey = ({
  isMultiSelection,
  isOneWay,
  isAddingDraft,
  isAngledHighway,
}: {
  isMultiSelection: boolean;
  isOneWay: boolean;
  isAddingDraft: boolean;
  isAngledHighway: boolean;
}): string | undefined => {
  if (isMultiSelection) return 'interface:jit_routing_points.disabled.multiple_selected';
  if (!isOneWay) return 'interface:jit_routing_points.disabled.plus_icon';
  if (isAddingDraft) return 'interface:jit_routing_points.disabled.draft_open';
  if (isAngledHighway) return 'interface:jit_routing_points.disabled.angled_highway';
  return undefined;
};

function JitRoutingPointPropertiesComponent({ selectedHighway, isMultiSelection }: Props) {
  const { t } = useTranslation('interface');
  const mode = useAppStore((state) => state.mode);
  const areas = useFloorplanDesignStore((state) => state.design.areas);
  const jitRoutingPoints = useFloorplanDesignStore(
    (state) => state.design.justInTimeRoutingConfigs,
  );
  const { id, highwayPoiSpecs, segments } = selectedHighway;
  const relatedJitRoutingPoints = useMemo(
    () =>
      jitRoutingPoints.filter((config) => segments.map((s) => s.id).includes(config.highwayAreaId)),
    [jitRoutingPoints, segments],
  );
  const { expanded = true, setExpanded } = useAccordionExpanded(
    UserPreferenceName.ACCORDION_PROPERTY_PANEL_HIGHWAY_JIT_ROUTING_POINTS,
  );
  const [showDraft, setShowDraft] = useState(false);

  const handleAddJitRoutingPoint = useCallback(() => {
    setShowDraft(true);
    setExpanded(true);
  }, [setExpanded]);

  const handleDismissDraft = useCallback(() => {
    setShowDraft(false);
  }, []);

  const isAngledHighway = segments.length > 1;

  const addDisabledReasonKey = getAddDisabledReasonKey({
    isMultiSelection,
    isOneWay: highwayPoiSpecs[0].roadType === 'ONE_WAY',
    isAddingDraft: showDraft,
    isAngledHighway,
  });
  // In read-only mode everything is disabled, so no reason tooltip is shown
  const isReadOnly = mode !== WorkspaceMode.EDITABLE;
  const isAddDisabled = isReadOnly || addDisabledReasonKey !== undefined || isAngledHighway;
  const addDisabledTooltip = !isReadOnly && addDisabledReasonKey ? t(addDisabledReasonKey) : '';

  if (!id || !highwayPoiSpecs) return null;

  // JIT routing points belong to one highway, so the list only shows for a single selection
  const showList = relatedJitRoutingPoints.length > 0 || showDraft || !isAngledHighway;

  return (
    <Stack sx={{ pt: 1 }} spacing={1}>
      <Accordion
        sx={{ px: 0 }}
        title={
          <Typography
            align='center'
            sx={{
              fontSize: '13px',
              fontWeight: 400,
            }}
          >
            {isMultiSelection
              ? t('jit_routing_points.header_multiple', 'Just-In-Time points')
              : t('jit_routing_points.header', 'Just-In-Time points', {
                  count: relatedJitRoutingPoints.length,
                })}
          </Typography>
        }
        expanded={expanded}
        onChange={setExpanded}
        afterIcon={
          <AccordionAddIconButton
            tooltip={addDisabledTooltip}
            disabled={isAddDisabled}
            onAdd={handleAddJitRoutingPoint}
            onOpen={() => setExpanded(true)}
          />
        }
      >
        {showList && (
          <JitRoutingPoints
            relatedJitRoutingPoints={relatedJitRoutingPoints}
            jitRoutingPoints={jitRoutingPoints}
            areas={areas}
            showDraft={showDraft}
            segmentId={segments[0].id}
            onDismissDraft={handleDismissDraft}
          />
        )}
      </Accordion>
    </Stack>
  );
}

export const JitRoutingPointProperties = memo(JitRoutingPointPropertiesComponent);
